import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from "rxjs";
import { fromPromise } from "rxjs/internal/observable/innerFrom";
import { HostUtil } from "../utils/host-util";
import { HttpClient } from "@angular/common/http";

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const httpClient = inject(HttpClient);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !request.url.includes("/v1/silent-login")) {
        return handleUnauthorizedError(request, next, httpClient);
      }

      return throwError(() => error);
    })
  );
};

function handleUnauthorizedError(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  httpClient: HttpClient
): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return fromPromise(silentLogin(httpClient)).pipe(
      switchMap((success: boolean) => {
        isRefreshing = false;
        refreshTokenSubject.next(success);

        if (success) {
          return next(request);
        }

        redirectToLogin();
        return throwError(() => new Error("Session expired"));
      }),
      catchError((err) => {
        isRefreshing = false;
        refreshTokenSubject.next(false);
        redirectToLogin();
        return throwError(() => err);
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter((result) => result !== null),
    take(1),
    switchMap((success) => {
      if (success) {
        return next(request);
      }
      return throwError(() => new Error("Session expired"));
    })
  );
}

async function silentLogin(httpClient: HttpClient): Promise<boolean> {
  const endpointUrl = `${HostUtil.getAuthenticationApiEndpointUrl()}/v1/silent-login`;

  try {
    await httpClient.get(endpointUrl, { withCredentials: true }).toPromise();
    return true;
  } catch {
    return false;
  }
}

function redirectToLogin(): void {
  location.href = HostUtil.getAuthenticationSiteUrl({"redirect": "developer-dashboard"});
}
